/**
 * EVCompareModal - EV-specific scenario comparison modal
 *
 * Compares multiple EV charging scenarios side-by-side with:
 * - Overview scoring rings (peak demand, cost, utilization, efficiency)
 * - Fleet configuration comparison
 * - Strategy cost breakdown across scenarios
 * - Load profile overlay charts
 * - Share functionality via ShareConfigDialog from shared-ui
 */

import React, { useState, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Button,
    Typography,
    IconButton,
    Chip,
    Tooltip,
    alpha,
} from '@mui/material';
import {
    Close as CloseIcon,
    EmojiEvents as TrophyIcon,
    BoltOutlined as PeakIcon,
    AttachMoney as CostIcon,
    EvStation as ChargerIcon,
    DirectionsCar as VehicleIcon,
    Share as ShareIcon,
} from '@mui/icons-material';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    AreaChart,
    Area,
} from 'recharts';
import {
    vortxColors,
    vortxTypography,
    useThemeMode,
    ShareConfigDialog,
} from '@vortx/shared-ui';

const SCENARIO_COLORS = [
    '#b28c50', // Vortx Gold
    '#5fd4c4', // Teal
    '#8b9dc3', // Muted Blue
    '#c4a35a', // Warm Gold
    '#6b9080', // Sage
];

const STRATEGY_LABELS = {
    opportunity: 'Opportunity',
    cost_optimized: 'Cost Optimized',
    demand_limit: 'Demand Limited',
};

function formatCurrency(value) {
    if (value === null || value === undefined) return '\u2014';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
}

function formatPower(value) {
    if (value === null || value === undefined) return '\u2014';
    return `${Math.round(value).toLocaleString()} kW`;
}

/**
 * ScoreRing — circular progress with centered score value
 */
function ScoreRing({ score, color, size = 60, isRetro }) {
    const radius = (size - 8) / 2;
    const circumference = 2 * Math.PI * radius;
    const progress = (score / 100) * circumference;

    return (
        <Box sx={{ position: 'relative', width: size, height: size }}>
            <svg width={size} height={size}>
                <circle
                    cx={size / 2} cy={size / 2} r={radius}
                    fill="none"
                    stroke={isRetro ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}
                    strokeWidth={4}
                />
                <circle
                    cx={size / 2} cy={size / 2} r={radius}
                    fill="none"
                    stroke={color}
                    strokeWidth={4}
                    strokeDasharray={circumference}
                    strokeDashoffset={circumference - progress}
                    strokeLinecap="round"
                    transform={`rotate(-90 ${size / 2} ${size / 2})`}
                />
            </svg>
            <Typography sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: '0.7rem',
                fontWeight: 700,
                color: isRetro ? '#f4f1ec' : vortxColors.charcoal,
                fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
            }}>
                {Math.round(score)}
            </Typography>
        </Box>
    );
}

/**
 * Section header for comparison sections
 */
function SectionHeader({ title, isRetro }) {
    return (
        <Typography sx={{
            fontFamily: isRetro ? vortxTypography.mono : vortxTypography.heading,
            fontSize: isRetro ? '0.75rem' : '0.9rem',
            fontWeight: 700,
            color: isRetro ? vortxColors.gold : vortxColors.charcoal,
            textTransform: isRetro ? 'uppercase' : 'none',
            letterSpacing: isRetro ? '0.1em' : 'normal',
            mb: 1.5,
            mt: 3,
            pb: 0.5,
            borderBottom: isRetro
                ? '1px solid rgba(178, 140, 80, 0.3)'
                : `1px solid ${alpha(vortxColors.gold, 0.3)}`,
        }}>
            {title}
        </Typography>
    );
}

export default function EVCompareModal({
    open,
    onClose,
    scenarios = [],
    projectId,
    projectName,
}) {
    const { isRetro } = useThemeMode();
    const [shareOpen, setShareOpen] = useState(false);

    // Extract best metrics from a scenario's results (multi-strategy or flat)
    const getMetrics = (s) => {
        const r = s.results || {};
        const strategies = r.strategies || {};
        for (const key of ['cost_optimized', 'demand_limit', 'opportunity']) {
            const strat = strategies[key];
            if (strat?.metrics) return strat.metrics;
        }
        return r.metrics || r;
    };

    // Compute scores for each scenario
    const scoredScenarios = useMemo(() => {
        if (scenarios.length === 0) return [];

        const metricsList = scenarios.map(getMetrics);
        const peaks = metricsList.map(m => m.peak_demand_kw || Infinity);
        const costs = metricsList.map(m => m.annual_total_cost || m.annual_cost || m.total_cost || Infinity);
        const minPeak = Math.min(...peaks.filter(v => v !== Infinity));
        const minCost = Math.min(...costs.filter(v => v !== Infinity));
        const maxPeak = Math.max(...peaks.filter(v => v !== Infinity));
        const maxCost = Math.max(...costs.filter(v => v !== Infinity));

        return scenarios.map((s, i) => {
            const m = metricsList[i];
            const peak = m.peak_demand_kw || maxPeak;
            const cost = m.annual_total_cost || m.annual_cost || m.total_cost || maxCost;
            const peakScore = maxPeak > minPeak
                ? 100 - (peak - minPeak) / (maxPeak - minPeak) * 100
                : 100;
            const costScore = maxCost > minCost
                ? 100 - (cost - minCost) / (maxCost - minCost) * 100
                : 100;
            const overallScore = (peakScore * 0.5 + costScore * 0.5);

            return {
                ...s,
                _metrics: m,
                _peak: peak,
                _cost: cost,
                color: SCENARIO_COLORS[i % SCENARIO_COLORS.length],
                scores: {
                    peak: peakScore,
                    cost: costScore,
                    overall: overallScore,
                },
            };
        }).sort((a, b) => b.scores.overall - a.scores.overall);
    }, [scenarios]);

    // Prepare strategy comparison chart data
    const strategyChartData = useMemo(() => {
        const strategies = ['opportunity', 'cost_optimized', 'demand_limit'];
        return strategies.map(strat => {
            const row = { strategy: STRATEGY_LABELS[strat] || strat };
            scoredScenarios.forEach((s) => {
                const stratMetrics = s.results?.strategies?.[strat]?.metrics;
                row[s.name] = stratMetrics?.annual_total_cost || stratMetrics?.total_cost || 0;
            });
            return row;
        });
    }, [scoredScenarios]);

    // Delta analysis: compare each scenario to the best (first after sort)
    const deltaAnalysis = useMemo(() => {
        if (scoredScenarios.length < 2) return [];
        const best = scoredScenarios[0];
        return scoredScenarios.slice(1).map(s => ({
            name: s.name,
            color: s.color,
            costDelta: s._cost - best._cost,
            costDeltaPct: best._cost > 0 ? ((s._cost - best._cost) / best._cost) * 100 : 0,
            peakDelta: s._peak - best._peak,
            peakDeltaPct: best._peak > 0 ? ((s._peak - best._peak) / best._peak) * 100 : 0,
            energyDelta: (s._metrics.total_energy_kwh || 0) - (best._metrics.total_energy_kwh || 0),
        }));
    }, [scoredScenarios]);

    // Prepare load profile overlay data
    const loadProfileData = useMemo(() => {
        const hours = Array.from({ length: 24 }, (_, i) => ({ hour: i }));
        scoredScenarios.forEach(s => {
            const loads = s.results?.daily_loads_hourly || s.results?.strategies?.cost_optimized?.daily_loads_hourly;
            if (loads && loads.length >= 24) {
                hours.forEach((h, i) => {
                    h[s.name] = loads[i] || 0;
                });
            }
        });
        return hours;
    }, [scoredScenarios]);

    const hasLoadData = loadProfileData.some(h =>
        Object.keys(h).some(k => k !== 'hour' && h[k] > 0)
    );

    const winner = scoredScenarios[0];

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="lg"
                fullWidth
                slotProps={{
                    paper: {
                        sx: isRetro ? {
                            bgcolor: 'rgba(10, 18, 16, 0.98)',
                            borderRadius: 0,
                            border: `1px solid ${vortxColors.gold}`,
                            boxShadow: `0 0 40px rgba(178, 140, 80, 0.3)`,
                        } : {
                            bgcolor: 'background.paper',
                            borderRadius: 3,
                        }
                    }
                }}
            >
                <DialogTitle sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontFamily: isRetro ? vortxTypography.heading : vortxTypography.body,
                    color: isRetro ? vortxColors.gold : vortxColors.charcoal,
                    fontWeight: 700,
                    borderBottom: isRetro
                        ? '1px solid rgba(178, 140, 80, 0.3)'
                        : `1px solid ${alpha(vortxColors.teal, 0.2)}`,
                }}>
                    <Box>
                        {isRetro ? 'EV Scenario Comparison' : 'Compare EV Scenarios'}
                        <Typography variant="body2" sx={{
                            color: isRetro ? '#888' : 'text.secondary',
                            fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                            fontSize: '0.75rem',
                        }}>
                            {projectName} \u2014 {scenarios.length} scenarios
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Share comparison">
                            <IconButton
                                onClick={() => setShareOpen(true)}
                                sx={{ color: isRetro ? '#5fd4c4' : vortxColors.teal }}
                            >
                                <ShareIcon />
                            </IconButton>
                        </Tooltip>
                        <IconButton onClick={onClose} sx={{ color: isRetro ? '#888' : 'text.secondary' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ pt: 2, px: 3 }}>
                    {/* Overview Cards */}
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                        {scoredScenarios.map((s, i) => (
                            <Box key={s.id} sx={{
                                flex: '1 1 200px',
                                maxWidth: 280,
                                p: 2,
                                borderRadius: isRetro ? 0 : 2,
                                bgcolor: isRetro ? 'rgba(0,0,0,0.4)' : alpha(vortxColors.warmStone, 0.15),
                                border: i === 0
                                    ? `2px solid ${s.color}`
                                    : isRetro ? '1px solid rgba(63,106,100,0.3)' : `1px solid ${alpha(vortxColors.teal, 0.15)}`,
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                    {i === 0 && <TrophyIcon sx={{ fontSize: 18, color: vortxColors.gold }} />}
                                    <Typography sx={{
                                        fontWeight: 700,
                                        fontSize: '0.85rem',
                                        color: isRetro ? s.color : vortxColors.charcoal,
                                        fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                                    }}>
                                        {s.name}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 1.5 }}>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <ScoreRing score={s.scores.peak} color={s.color} isRetro={isRetro} />
                                        <Typography sx={{ fontSize: '0.6rem', mt: 0.5, color: isRetro ? '#888' : 'text.secondary' }}>
                                            Peak
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <ScoreRing score={s.scores.cost} color={s.color} isRetro={isRetro} />
                                        <Typography sx={{ fontSize: '0.6rem', mt: 0.5, color: isRetro ? '#888' : 'text.secondary' }}>
                                            Cost
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem' }}>
                                    <Typography sx={{ fontSize: 'inherit', color: isRetro ? '#888' : 'text.secondary' }}>
                                        Peak: {formatPower(s._peak)}
                                    </Typography>
                                    <Typography sx={{ fontSize: 'inherit', color: isRetro ? '#888' : 'text.secondary' }}>
                                        Cost: {formatCurrency(s._cost)}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>

                    {/* Fleet Configuration */}
                    <SectionHeader title="Fleet & Charger Configuration" isRetro={isRetro} />
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {scoredScenarios.map(s => (
                            <Box key={s.id} sx={{
                                flex: '1 1 180px',
                                p: 1.5,
                                borderRadius: isRetro ? 0 : 1,
                                bgcolor: isRetro ? 'rgba(0,0,0,0.3)' : alpha(vortxColors.warmStone, 0.1),
                                borderLeft: `3px solid ${s.color}`,
                            }}>
                                <Typography sx={{
                                    fontWeight: 600,
                                    fontSize: '0.8rem',
                                    color: isRetro ? s.color : vortxColors.charcoal,
                                    fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                                    mb: 1,
                                }}>
                                    {s.name}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                    <VehicleIcon sx={{ fontSize: 14, color: isRetro ? '#888' : 'text.secondary' }} />
                                    <Typography sx={{ fontSize: '0.7rem', color: isRetro ? '#888' : 'text.secondary' }}>
                                        {s.vehicles?.length || 0} vehicles
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <ChargerIcon sx={{ fontSize: 14, color: isRetro ? '#888' : 'text.secondary' }} />
                                    <Typography sx={{ fontSize: '0.7rem', color: isRetro ? '#888' : 'text.secondary' }}>
                                        {(s.chargers || []).map(c => `${c.quantity || 1}x ${c.power_kw || 0}kW`).join(', ') || 'No chargers'}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>

                    {/* Strategy Cost Comparison */}
                    {strategyChartData.some(d => Object.values(d).some(v => typeof v === 'number' && v > 0)) && (
                        <>
                            <SectionHeader title="Strategy Cost Comparison" isRetro={isRetro} />
                            <Box sx={{ width: '100%', height: 250 }}>
                                <ResponsiveContainer>
                                    <BarChart data={strategyChartData}>
                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                            stroke={isRetro ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}
                                        />
                                        <XAxis
                                            dataKey="strategy"
                                            tick={{ fontSize: 11, fill: isRetro ? '#888' : vortxColors.charcoal }}
                                        />
                                        <YAxis
                                            tick={{ fontSize: 11, fill: isRetro ? '#888' : vortxColors.charcoal }}
                                            tickFormatter={v => `$${(v / 1000).toFixed(0)}k`}
                                        />
                                        <RechartsTooltip
                                            formatter={(value) => formatCurrency(value)}
                                            contentStyle={{
                                                backgroundColor: isRetro ? '#1a1a1a' : '#fff',
                                                border: isRetro ? '1px solid rgba(178,140,80,0.3)' : '1px solid #ddd',
                                                color: isRetro ? '#f4f1ec' : '#333',
                                                fontFamily: vortxTypography.body,
                                                fontSize: 12,
                                            }}
                                        />
                                        <Legend />
                                        {scoredScenarios.map((s, i) => (
                                            <Bar
                                                key={s.id}
                                                dataKey={s.name}
                                                fill={s.color}
                                                radius={[2, 2, 0, 0]}
                                            />
                                        ))}
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                        </>
                    )}

                    {/* Load Profile Overlay */}
                    {hasLoadData && (
                        <>
                            <SectionHeader title="Daily Load Profile Comparison" isRetro={isRetro} />
                            <Box sx={{ width: '100%', height: 250 }}>
                                <ResponsiveContainer>
                                    <AreaChart data={loadProfileData}>
                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                            stroke={isRetro ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}
                                        />
                                        <XAxis
                                            dataKey="hour"
                                            tick={{ fontSize: 11, fill: isRetro ? '#888' : vortxColors.charcoal }}
                                            tickFormatter={h => `${h}:00`}
                                        />
                                        <YAxis
                                            tick={{ fontSize: 11, fill: isRetro ? '#888' : vortxColors.charcoal }}
                                            tickFormatter={v => `${v} kW`}
                                        />
                                        <RechartsTooltip
                                            formatter={(value, name) => [`${Math.round(value)} kW`, name]}
                                            contentStyle={{
                                                backgroundColor: isRetro ? '#1a1a1a' : '#fff',
                                                border: isRetro ? '1px solid rgba(178,140,80,0.3)' : '1px solid #ddd',
                                                color: isRetro ? '#f4f1ec' : '#333',
                                                fontFamily: vortxTypography.body,
                                                fontSize: 12,
                                            }}
                                        />
                                        <Legend />
                                        {scoredScenarios.map((s, i) => (
                                            <Area
                                                key={s.id}
                                                type="monotone"
                                                dataKey={s.name}
                                                stroke={s.color}
                                                fill={s.color}
                                                fillOpacity={0.15}
                                                strokeWidth={2}
                                            />
                                        ))}
                                    </AreaChart>
                                </ResponsiveContainer>
                            </Box>
                        </>
                    )}

                    {/* Delta Analysis */}
                    {deltaAnalysis.length > 0 && (
                        <>
                            <SectionHeader title={`Delta vs Best (${scoredScenarios[0]?.name})`} isRetro={isRetro} />
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                {deltaAnalysis.map(d => (
                                    <Box key={d.name} sx={{
                                        flex: '1 1 200px',
                                        maxWidth: 300,
                                        p: 2,
                                        borderRadius: isRetro ? 0 : 2,
                                        bgcolor: isRetro ? 'rgba(0,0,0,0.3)' : alpha(vortxColors.warmStone, 0.1),
                                        borderLeft: `3px solid ${d.color}`,
                                    }}>
                                        <Typography sx={{
                                            fontWeight: 700,
                                            fontSize: '0.85rem',
                                            color: isRetro ? d.color : vortxColors.charcoal,
                                            fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                                            mb: 1,
                                        }}>
                                            {d.name}
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, fontSize: '0.75rem' }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography sx={{ fontSize: 'inherit', color: isRetro ? '#888' : 'text.secondary' }}>
                                                    Cost
                                                </Typography>
                                                <Typography sx={{
                                                    fontSize: 'inherit',
                                                    fontWeight: 600,
                                                    color: d.costDelta > 0 ? '#d32f2f' : '#2e7d32',
                                                }}>
                                                    {d.costDelta > 0 ? '+' : ''}{formatCurrency(d.costDelta)} ({d.costDeltaPct > 0 ? '+' : ''}{d.costDeltaPct.toFixed(1)}%)
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography sx={{ fontSize: 'inherit', color: isRetro ? '#888' : 'text.secondary' }}>
                                                    Peak
                                                </Typography>
                                                <Typography sx={{
                                                    fontSize: 'inherit',
                                                    fontWeight: 600,
                                                    color: d.peakDelta > 0 ? '#d32f2f' : '#2e7d32',
                                                }}>
                                                    {d.peakDelta > 0 ? '+' : ''}{formatPower(d.peakDelta)} ({d.peakDeltaPct > 0 ? '+' : ''}{d.peakDeltaPct.toFixed(1)}%)
                                                </Typography>
                                            </Box>
                                            {d.energyDelta !== 0 && (
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography sx={{ fontSize: 'inherit', color: isRetro ? '#888' : 'text.secondary' }}>
                                                        Energy
                                                    </Typography>
                                                    <Typography sx={{ fontSize: 'inherit', fontWeight: 600, color: isRetro ? '#ccc' : 'text.primary' }}>
                                                        {d.energyDelta > 0 ? '+' : ''}{Math.round(d.energyDelta).toLocaleString()} kWh
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </>
                    )}
                </DialogContent>

                <DialogActions sx={{
                    px: 3,
                    py: 2,
                    borderTop: isRetro
                        ? '1px solid rgba(63, 106, 100, 0.3)'
                        : `1px solid ${alpha(vortxColors.teal, 0.15)}`,
                }}>
                    <Button
                        onClick={onClose}
                        sx={isRetro ? {
                            color: '#888',
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            borderRadius: 0,
                        } : {
                            color: 'text.secondary',
                            textTransform: 'none',
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Share Dialog */}
            <ShareConfigDialog
                open={shareOpen}
                onClose={() => setShareOpen(false)}
                projectId={projectId}
                projectName={projectName}
                scenarioIds={scenarios.map(s => s.id)}
                scenarioNames={scenarios.map(s => s.name)}
                shareType="scenario_comparison"
            />
        </>
    );
}
